import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import MenuCard from '../components/MenuCard';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import DailySpecial from '../components/DailySpecial';
import { resolveImg, thumbImg } from '../utils/resolveImg';
import SocialLinks from '../components/SocialLinks';
import { tr } from '../i18n';
import { MapPin, Phone, Clock, QrCode, Settings, FileText, Menu as MenuIcon, X } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const { menu, lang, t, track } = useApp();
  const { mainCategories, restaurantInfo, dailySpecial } = menu;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Track home view once per mount
  useEffect(() => {
    track('home');
  }, [track]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const toolItems = [
    { icon: QrCode, label: lang === 'it' ? 'QR del menù' : 'Menu QR', to: '/qr' },
    { icon: FileText, label: lang === 'it' ? 'Versione stampabile' : 'Printable version', to: '/stampa' },
    { icon: Settings, label: lang === 'it' ? 'Pannello admin' : 'Admin panel', to: '/admin' },
  ];

  return (
    <div className="relative min-h-screen w-full pt-6 pb-24 px-4 md:px-10">
      <div className="relative max-w-6xl mx-auto">
        {/* Top bar - mobile: logo top center, icons below; desktop: single row */}
        <div className="mb-6">
          {/* Logo row (mobile centered) */}
          <div className="flex justify-center md:hidden mb-4">
            <button
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition outline-none focus-visible:ring-2 focus-visible:ring-black rounded"
              aria-label="Home"
            >
              <Logo height={72} />
            </button>
          </div>
          {/* Controls row */}
          <div className="flex items-center justify-between gap-2">
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                data-testid="home-tools-menu-btn"
                className="w-11 h-11 rounded-full border-[1.5px] border-black flex items-center justify-center hover:bg-black hover:text-white transition"
                aria-label={lang === 'it' ? 'Strumenti' : 'Tools'}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                {menuOpen ? <X size={18} /> : <MenuIcon size={18} />}
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 mt-2 bg-white border-[1.5px] border-black rounded-2xl shadow-[0_12px_28px_-10px_rgba(0,0,0,0.35)] py-1 min-w-[220px] z-30 overflow-hidden"
                >
                  {toolItems.map((it) => {
                    const Icon = it.icon;
                    return (
                      <button
                        key={it.to}
                        role="menuitem"
                        onClick={() => { setMenuOpen(false); navigate(it.to); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold hover:bg-black hover:text-white transition"
                      >
                        <Icon size={16} /> {it.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/')}
              className="hidden md:inline-flex hover:opacity-80 transition outline-none focus-visible:ring-2 focus-visible:ring-black rounded"
              aria-label="Home"
            >
              <Logo height={92} />
            </button>
            <LanguageSwitcher />
          </div>
        </div>

        {/* Hero */}
        <div className="flex flex-col items-center text-center pt-6 pb-12">
          <p className="text-xs md:text-sm font-bold uppercase tracking-[0.32em] text-black/60 mb-4">{lang === 'it' ? 'Pinseria & Birre artigianali' : 'Pinseria & Craft beers'}</p>
          <h1 className="font-display text-[72px] md:text-[128px] leading-[0.9] text-black">
            {lang === 'it' ? (<>Il <span className="font-serif-alt">nostro</span> Menù</>) : (<>Our <span className="font-serif-alt">Menu</span></>)}
          </h1>
          <p className="mt-5 text-black/70 text-base md:text-lg max-w-xl">
            {tr(restaurantInfo.tagline, lang)}
          </p>
          <div className="mt-6 hairline w-16" />
        </div>

        {/* Daily special (shown only when active today) */}
        <DailySpecial special={dailySpecial} lang={lang} />

        {/* Category cards */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-8">
          {mainCategories.map((cat, idx) => (
            <div key={cat.slug} className="float-in" style={{ animationDelay: `${idx * 100}ms` }}>
              <MenuCard
                image={thumbImg(cat.image, 640)}
                fallback={resolveImg(cat.image)}
                label={tr(cat.name, lang)}
                onClick={() => navigate(`/categoria/${cat.slug}`)}
                buttonLabel={t('seleziona')}
              />
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="mt-20 flex flex-col items-center text-center gap-5 text-black">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-10 w-full max-w-4xl">
            <InfoBlock icon={<MapPin size={18} />} label={lang === 'it' ? 'Dove siamo' : 'Address'} value={restaurantInfo.address} />
            <InfoBlock icon={<Phone size={18} />} label={lang === 'it' ? 'Telefono' : 'Phone'} value={restaurantInfo.phone} />
            <InfoBlock icon={<Clock size={18} />} label={lang === 'it' ? 'Orari' : 'Opening hours'} value={tr(restaurantInfo.hours, lang)} />
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-3">
            <a
              href={restaurantInfo.reserveUrl || "https://ilponticello.plateform.app/welcome"}
              target="_blank"
              rel="noreferrer"
              className="pill-btn primary"
            >
              {t('prenotaOra')}
            </a>
            <a href={restaurantInfo.websiteUrl || "https://www.ilponticello.org"} target="_blank" rel="noreferrer" className="pill-btn">{t('scopri')}</a>
          </div>
          <SocialLinks info={restaurantInfo} className="mt-2" />
        </div>

        {/* Marquee word decoration */}
        <div className="mt-24 text-center overflow-hidden">
          <div className="marquee-txt text-[24vw] md:text-[16vw]">ponticello</div>
        </div>
      </div>
    </div>
  );
};

const InfoBlock = ({ icon, label, value }) => (
  <div className="flex flex-col items-center md:items-start text-center md:text-left gap-1">
    <div className="flex items-center gap-2 text-black/60 font-bold uppercase tracking-widest text-[10px]">
      {icon} <span>{label}</span>
    </div>
    <div className="font-semibold text-black text-sm md:text-base">{value}</div>
  </div>
);

export default Home;
